import React, { useEffect, useMemo, useState } from "react"
import { BookShell } from "../components/book-shell"
import { FORM_INPUT_CLASS } from "../components/currency-search-select"
import { PageHeader } from "../components/page-header"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { appConfirm } from "../lib/app-dialogs"
import { accessFromProps } from "../lib/book-access"
import { usePageLoading } from "../lib/page-loading"
import { cn } from "../lib/utils"

const FIELD_CONTROL_CLASS = cn(
  FORM_INPUT_CLASS,
  "box-border min-h-9 max-h-9 py-0 leading-9",
  "[&[type=date]]:min-h-9 [&[type=date]]:max-h-9 [&[type=date]]:py-0 [&[type=date]]:leading-9",
  "[&::-webkit-datetime-edit]:p-0 [&::-webkit-datetime-edit]:leading-9",
  "[&::-webkit-calendar-picker-indicator]:my-0 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4",
)

function currentOrganizationIdFromMeta() {
  const raw =
    typeof document !== "undefined"
      ? document.querySelector("meta[name='current-organization-id']")?.getAttribute("content")
      : null
  if (raw == null || String(raw).trim() === "") return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
    ...options,
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.message || data?.error || `Request failed (${res.status})`
    const details = data?.errors ? (Array.isArray(data.errors) ? data.errors.join(", ") : JSON.stringify(data.errors)) : ""
    throw new Error(details ? `${message}: ${details}` : message)
  }
  return data
}

function TextInput({ label, value, onChange, type = "text", placeholder, error, readOnly = false, hint, className }) {
  return (
    <label className={cn("grid gap-0.5 text-xs", className)}>
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className={cn(FIELD_CONTROL_CLASS, readOnly && "cursor-default bg-muted/30 text-muted-foreground")}
      />
      {hint ? <span className="text-[11px] leading-snug text-muted-foreground">{hint}</span> : null}
      {error ? <span className="text-[12px] text-red-600">{error}</span> : null}
    </label>
  )
}

function SelectInput({ label, value, onChange, options, error, hint, disabled = false, includeBlank = true, className }) {
  return (
    <label className={cn("grid gap-0.5 text-xs", className)}>
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(FIELD_CONTROL_CLASS, disabled && "cursor-default bg-muted/20")}
      >
        {includeBlank ? <option value="">Select...</option> : null}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint ? <span className="text-[11px] leading-snug text-muted-foreground">{hint}</span> : null}
      {error ? <span className="text-xs text-red-600">{error}</span> : null}
    </label>
  )
}

const EMPTY_EMPLOYEE = {
  name: "",
  email: "",
  phone: "",
  date_of_birth: "",
  pan: "",
  address: "",
  department: "",
  designation: "",
  date_of_joining: "",
  employment_status: "active",
  employee_number: "",
}

const EMPTY_BANKING = {
  account_holder_name: "",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  branch_name: "",
  account_type: "savings",
  primary_account: true,
}

const EMPTY_NOMINEE = {
  name: "",
  relationship: "",
  date_of_birth: "",
  phone: "",
  address: "",
  share_percent: "",
}

function centsToAmount(cents) {
  if (cents === null || cents === undefined) return ""
  return (Number(cents) / 100).toFixed(2)
}

function amountToCents(value) {
  const n = Number.parseFloat(String(value || "0"))
  if (Number.isNaN(n)) return 0
  return Math.round(n * 100)
}

function todayIso() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const EMPTY_SALARY = {
  effective_from: todayIso(),
  base_salary: "",
  hra: "",
  other_allowances: "",
  monthly_bonus: "",
  pf: "",
  esi: "",
  tds: "",
  other_deduction: "",
  notes: "",
}

function organizationLabel(org) {
  if (!org) return ""
  return org.company_code ? `${org.name} (${org.company_code})` : org.name
}

function EmployeesSection({ canManage, currentOrganization }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY_EMPLOYEE)
  const [numberLoading, setNumberLoading] = useState(false)
  const [organizations, setOrganizations] = useState(() =>
    currentOrganization?.id ? [currentOrganization] : [],
  )
  const [selectedOrgId, setSelectedOrgId] = useState(
    () => currentOrganization?.id || currentOrganizationIdFromMeta() || "",
  )
  const [orgSwitching, setOrgSwitching] = useState(false)

  async function load() {
    try {
      setLoading(true)
      setError("")
      const data = await fetchJson("/api/hr/employees")
      setItems(Array.isArray(data) ? data : [])
    } catch (e) {
      setError(e.message || "Failed to load employees")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const data = await fetchJson("/api/organizations")
        if (!alive) return
        const list = Array.isArray(data) ? data : []
        setOrganizations(list)
        const metaId = currentOrganizationIdFromMeta()
        const match =
          list.find((o) => Number(o.id) === Number(metaId)) ||
          list.find((o) => Number(o.id) === Number(currentOrganization?.id)) ||
          list[0]
        if (match) setSelectedOrgId(match.id)
      } catch {
        /* keep the logged-in company from page props */
      }
    })()
    return () => {
      alive = false
    }
  }, [currentOrganization?.id])

  const selectedOrganization =
    organizations.find((o) => Number(o.id) === Number(selectedOrgId)) ||
    currentOrganization ||
    organizations[0] ||
    null
  const organizationOptions = organizations.map((o) => ({
    value: String(o.id),
    label: organizationLabel(o),
  }))

  async function switchCompany(id) {
    const nextId = Number(id)
    if (!nextId || Number.isNaN(nextId) || nextId === Number(selectedOrgId)) return
    const allowed = organizations.some((o) => Number(o.id) === nextId)
    if (!allowed) return
    try {
      setOrgSwitching(true)
      setError("")
      await fetchJson(`/api/organizations/${nextId}/select`, { method: "POST", body: "{}" })
      window.location.reload()
    } catch (e) {
      setError(e.message || "Could not switch company")
      setOrgSwitching(false)
    }
  }

  async function refreshNumber() {
    if (editingId) return
    try {
      setNumberLoading(true)
      const data = await fetchJson("/api/hr/employees/next_number")
      setForm((f) => ({ ...f, employee_number: data.employee_number || "" }))
    } catch {
      setForm((f) => ({ ...f, employee_number: "" }))
    } finally {
      setNumberLoading(false)
    }
  }

  useEffect(() => {
    if (!editingId) refreshNumber()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId])

  function resetForm() {
    setEditingId(null)
    setForm(EMPTY_EMPLOYEE)
    refreshNumber()
  }

  function hydrate(item) {
    setEditingId(item.id)
    setForm({
      name: item.name || "",
      email: item.email || "",
      phone: item.phone || "",
      date_of_birth: item.date_of_birth || "",
      pan: item.pan || "",
      address: item.address || "",
      department: item.department || "",
      designation: item.designation || "",
      date_of_joining: item.date_of_joining || "",
      employment_status: item.employment_status || "active",
      employee_number: item.employee_number || "",
    })
  }

  async function onSubmit(e) {
    e.preventDefault()
    if (!canManage) return
    try {
      setError("")
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        date_of_birth: form.date_of_birth || null,
        pan: form.pan.trim().toUpperCase() || null,
        address: form.address.trim() || null,
        department: form.department.trim() || null,
        designation: form.designation.trim() || null,
        date_of_joining: form.date_of_joining || null,
        employment_status: form.employment_status || "active",
      }
      const body = JSON.stringify({ employee: payload })
      if (editingId) {
        await fetchJson(`/api/hr/employees/${editingId}`, { method: "PUT", body })
      } else {
        await fetchJson("/api/hr/employees", { method: "POST", body })
      }
      await load()
      resetForm()
    } catch (e2) {
      setError(e2.message || "Save failed")
    }
  }

  async function onDelete(id) {
    if (!canManage || !(await appConfirm("Remove this employee record?", { title: "Remove employee", danger: true, confirmLabel: "Remove" }))) return
    try {
      await fetchJson(`/api/hr/employees/${id}`, { method: "DELETE" })
      await load()
      if (editingId === id) resetForm()
    } catch (e) {
      setError(e.message || "Delete failed")
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {canManage ? (
        <Card id="employee-register-form">
          <CardHeader>
            <CardTitle>{editingId ? "Edit employee" : "Register employee"}</CardTitle>
          </CardHeader>
          <CardContent>
            {error ? <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
            <form onSubmit={onSubmit} className="grid items-start gap-x-4 gap-y-4 md:grid-cols-3">
              <SelectInput
                className="md:col-span-3"
                label="Company"
                value={selectedOrgId ? String(selectedOrgId) : ""}
                onChange={switchCompany}
                includeBlank={false}
                disabled={orgSwitching || organizationOptions.length <= 1}
                options={
                  organizationOptions.length > 0
                    ? organizationOptions
                    : selectedOrganization
                      ? [{ value: String(selectedOrganization.id), label: organizationLabel(selectedOrganization) }]
                      : [{ value: "", label: "Loading company…" }]
                }
                hint={
                  orgSwitching
                    ? "Switching company…"
                    : selectedOrganization
                      ? `The employee will be registered under ${selectedOrganization.name}.`
                      : "The employee will be registered under the company selected in the top bar."
                }
              />
              <TextInput
                label="Employee number"
                value={form.employee_number}
                onChange={() => {}}
                readOnly
                hint={
                  numberLoading
                    ? "Generating from company code and database sequence…"
                    : "Format: COMPANY-EMP-##### (auto-assigned on save)."
                }
              />
              <TextInput label="Full name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
              <TextInput label="Email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
              <TextInput label="Phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
              <TextInput
                label="Date of birth"
                type="date"
                value={form.date_of_birth}
                onChange={(v) => setForm((f) => ({ ...f, date_of_birth: v }))}
              />
              <TextInput
                label="PAN"
                value={form.pan}
                placeholder="e.g. ABCDE1234F"
                onChange={(v) => setForm((f) => ({ ...f, pan: v }))}
                hint="Permanent Account Number (tax ID)."
              />
              <TextInput label="Department" value={form.department} onChange={(v) => setForm((f) => ({ ...f, department: v }))} />
              <TextInput label="Designation" value={form.designation} onChange={(v) => setForm((f) => ({ ...f, designation: v }))} />
              <TextInput
                label="Date of joining"
                type="date"
                value={form.date_of_joining}
                onChange={(v) => setForm((f) => ({ ...f, date_of_joining: v }))}
              />
              <SelectInput
                label="Status"
                value={form.employment_status}
                onChange={(v) => setForm((f) => ({ ...f, employment_status: v }))}
                includeBlank={false}
                options={[
                  { value: "active", label: "Active" },
                  { value: "on_leave", label: "On leave" },
                  { value: "inactive", label: "Inactive" },
                  { value: "terminated", label: "Terminated" },
                ]}
              />
              <TextInput
                className="md:col-span-2"
                label="Address"
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
              />
              <div className="flex gap-2 md:col-span-3">
                <Button type="submit">{editingId ? "Save changes" : "Register employee"}</Button>
                {editingId ? (
                  <Button type="button" variant="ghost" onClick={resetForm}>
                    Cancel
                  </Button>
                ) : null}
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">You have read-only access to employee records.</CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Employees</CardTitle>
          <span className="text-xs text-muted-foreground">
            {loading
              ? "Loading…"
              : `${items.length} registered${selectedOrganization?.name ? ` · ${selectedOrganization.name}` : ""}`}
          </span>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-xl border border-border/90">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                  <th className="px-2 py-1.5">Employee #</th>
                  <th className="px-2 py-1.5">Name</th>
                  <th className="px-2 py-1.5">Company</th>
                  <th className="px-2 py-1.5">Department</th>
                  <th className="px-2 py-1.5">Phone</th>
                  <th className="px-2 py-1.5">Status</th>
                  {canManage ? <th className="px-2 py-1.5">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-border/60 hover:bg-muted/20">
                    <td className="px-2 py-1 font-mono text-[11px]">{item.employee_number}</td>
                    <td className="px-2 py-1">{item.name}</td>
                    <td className="px-2 py-1">
                      {item.organization?.name || item.organization_name || selectedOrganization?.name || "—"}
                    </td>
                    <td className="px-2 py-1">{item.department || "—"}</td>
                    <td className="px-2 py-1">{item.phone || "—"}</td>
                    <td className="px-2 py-1 capitalize">{item.employment_status}</td>
                    {canManage ? (
                      <td className="px-2 py-1">
                        <div className="flex gap-1">
                          <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs" onClick={() => hydrate(item)}>
                            Edit
                          </Button>
                          <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs text-red-600" onClick={() => onDelete(item.id)}>
                            Delete
                          </Button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
                {!loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={canManage ? 7 : 6} className="px-2 py-3 text-muted-foreground">
                      No employees yet. Register the first employee above.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function SalaryStructuresSection({ canManage, employees }) {
  const [employeeId, setEmployeeId] = useState("")
  const [form, setForm] = useState(EMPTY_SALARY)
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!employeeId) {
      setForm(EMPTY_SALARY)
      return
    }
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        setError("")
        setSaved(false)
        const data = await fetchJson(`/api/hr/employees/${employeeId}/salary_structure`)
        const s = data?.employee_salary_structure ?? data
        if (!alive) return
        if (!s || !s.id) {
          setForm({ ...EMPTY_SALARY, effective_from: todayIso() })
          return
        }
        setForm({
          effective_from: s.effective_from || todayIso(),
          base_salary: centsToAmount(s.base_salary_cents),
          hra: centsToAmount(s.hra_cents),
          other_allowances: centsToAmount(s.other_allowances_cents),
          monthly_bonus: centsToAmount(s.monthly_bonus_cents),
          pf: centsToAmount(s.pf_cents),
          esi: centsToAmount(s.esi_cents),
          tds: centsToAmount(s.tds_cents),
          other_deduction: centsToAmount(s.other_deduction_cents),
          notes: s.notes || "",
        })
      } catch (e) {
        if (alive) setError(e.message || "Failed to load salary structure")
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [employeeId])

  async function onSubmit(e) {
    e.preventDefault()
    if (!employeeId || !canManage) return
    try {
      setError("")
      setSaved(false)
      const body = JSON.stringify({
        employee_salary_structure: {
          effective_from: form.effective_from,
          base_salary_cents: amountToCents(form.base_salary),
          hra_cents: amountToCents(form.hra),
          other_allowances_cents: amountToCents(form.other_allowances),
          monthly_bonus_cents: amountToCents(form.monthly_bonus),
          pf_cents: amountToCents(form.pf),
          esi_cents: amountToCents(form.esi),
          tds_cents: amountToCents(form.tds),
          other_deduction_cents: amountToCents(form.other_deduction),
          notes: form.notes,
        },
      })
      await fetchJson(`/api/hr/employees/${employeeId}/salary_structure`, { method: "PATCH", body })
      setSaved(true)
    } catch (err) {
      setError(err.message || "Save failed")
    }
  }

  const previewGross =
    amountToCents(form.base_salary) +
    amountToCents(form.hra) +
    amountToCents(form.other_allowances) +
    amountToCents(form.monthly_bonus)
  const previewDed =
    amountToCents(form.pf) + amountToCents(form.esi) + amountToCents(form.tds) + amountToCents(form.other_deduction)
  const previewNet = previewGross - previewDed

  return (
    <Card>
      <CardHeader>
        <CardTitle>Salary structures</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <p className="text-sm text-muted-foreground">
          Monthly amounts used when building payroll runs. Allowances (HRA + other) roll into the payroll line allowance column.
        </p>
        <EmployeePicker employees={employees} selectedId={employeeId} onChange={setEmployeeId} />
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        {loading ? <p className="text-xs text-muted-foreground">Loading…</p> : null}
        {canManage && employeeId && !loading ? (
          <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-2">
            <TextInput label="Effective from" type="date" value={form.effective_from} onChange={(v) => setForm((f) => ({ ...f, effective_from: v }))} />
            <TextInput label="Base salary (₹)" value={form.base_salary} onChange={(v) => setForm((f) => ({ ...f, base_salary: v }))} />
            <TextInput label="HRA (₹)" value={form.hra} onChange={(v) => setForm((f) => ({ ...f, hra: v }))} />
            <TextInput label="Other allowances (₹)" value={form.other_allowances} onChange={(v) => setForm((f) => ({ ...f, other_allowances: v }))} />
            <TextInput label="Monthly bonus (₹)" value={form.monthly_bonus} onChange={(v) => setForm((f) => ({ ...f, monthly_bonus: v }))} />
            <TextInput label="PF deduction (₹)" value={form.pf} onChange={(v) => setForm((f) => ({ ...f, pf: v }))} />
            <TextInput label="ESI deduction (₹)" value={form.esi} onChange={(v) => setForm((f) => ({ ...f, esi: v }))} />
            <TextInput label="TDS (₹)" value={form.tds} onChange={(v) => setForm((f) => ({ ...f, tds: v }))} />
            <TextInput label="Other deductions (₹)" value={form.other_deduction} onChange={(v) => setForm((f) => ({ ...f, other_deduction: v }))} />
            <div className="md:col-span-2">
              <TextInput label="Notes" value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} />
            </div>
            <p className="md:col-span-2 text-xs text-muted-foreground">
              Preview net: ₹{(previewNet / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })} (gross ₹
              {(previewGross / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })} − deductions ₹
              {(previewDed / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })})
            </p>
            <Button type="submit" className="md:col-span-2">
              Save salary structure
            </Button>
            {saved ? <p className="md:col-span-2 text-xs text-emerald-700">Saved.</p> : null}
          </form>
        ) : null}
      </CardContent>
    </Card>
  )
}

function EmployeePicker({ employees, selectedId, onChange }) {
  const options = employees.map((e) => ({
    value: String(e.id),
    label: `${e.employee_number} — ${e.name}`,
  }))
  return <SelectInput label="Employee" value={selectedId} onChange={onChange} options={options} />
}

function BankingSection({ canManage, employees }) {
  const [employeeId, setEmployeeId] = useState("")
  const [profiles, setProfiles] = useState([])
  const [form, setForm] = useState(EMPTY_BANKING)
  const [editingId, setEditingId] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)

  useEffect(() => {
    if (!employeeId) {
      setProfiles([])
      setLoading(false)
      return
    }
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        const data = await fetchJson(`/api/hr/employees/${employeeId}/banking_profiles`)
        if (alive) setProfiles(Array.isArray(data) ? data : [])
      } catch (e) {
        if (alive) setError(e.message || "Failed to load banking details")
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [employeeId])

  async function onSubmit(e) {
    e.preventDefault()
    if (!canManage || !employeeId) return
    try {
      setError("")
      const body = JSON.stringify({
        employee_banking_profile: {
          ...form,
          primary_account: Boolean(form.primary_account),
        },
      })
      if (editingId) {
        await fetchJson(`/api/hr/employees/${employeeId}/banking_profiles/${editingId}`, { method: "PUT", body })
      } else {
        await fetchJson(`/api/hr/employees/${employeeId}/banking_profiles`, { method: "POST", body })
      }
      const data = await fetchJson(`/api/hr/employees/${employeeId}/banking_profiles`)
      setProfiles(data)
      setForm(EMPTY_BANKING)
      setEditingId(null)
    } catch (e2) {
      setError(e2.message || "Save failed")
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Bank account details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <p className="text-sm text-muted-foreground">
            Phase 2 — salary and bonus payments will use these accounts. Select an employee, then add or update their bank details.
          </p>
          <EmployeePicker employees={employees} selectedId={employeeId} onChange={setEmployeeId} />
          {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
          {canManage && employeeId ? (
            <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-2">
              <TextInput label="Account holder name" value={form.account_holder_name} onChange={(v) => setForm((f) => ({ ...f, account_holder_name: v }))} />
              <TextInput label="Bank name" value={form.bank_name} onChange={(v) => setForm((f) => ({ ...f, bank_name: v }))} />
              <TextInput label="Account number" value={form.account_number} onChange={(v) => setForm((f) => ({ ...f, account_number: v }))} />
              <TextInput label="IFSC" value={form.ifsc_code} onChange={(v) => setForm((f) => ({ ...f, ifsc_code: v }))} />
              <TextInput label="Branch" value={form.branch_name} onChange={(v) => setForm((f) => ({ ...f, branch_name: v }))} />
              <SelectInput
                label="Account type"
                value={form.account_type}
                onChange={(v) => setForm((f) => ({ ...f, account_type: v }))}
                options={[
                  { value: "savings", label: "Savings" },
                  { value: "current", label: "Current" },
                ]}
              />
              <label className="flex items-center gap-2 text-xs md:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(form.primary_account)}
                  onChange={(e) => setForm((f) => ({ ...f, primary_account: e.target.checked }))}
                />
                Primary salary account
              </label>
              <Button type="submit" className="md:col-span-2">
                {editingId ? "Update bank account" : "Save bank account"}
              </Button>
            </form>
          ) : null}
          {profiles.length > 0 ? (
            <ul className="space-y-2 text-xs">
              {profiles.map((p) => (
                <li key={p.id} className="rounded-lg border border-border/90 p-2">
                  <div className="font-medium">
                    {p.bank_name} · {p.account_number}
                    {p.primary_account ? " (primary)" : ""}
                  </div>
                  <div className="text-muted-foreground">IFSC {p.ifsc_code || "—"} · {p.account_holder_name}</div>
                  {canManage ? (
                    <Button
                      type="button"
                      variant="ghost"
                      className="mt-1 h-7 px-1.5 text-xs"
                      onClick={() => {
                        setEditingId(p.id)
                        setForm({
                          account_holder_name: p.account_holder_name || "",
                          bank_name: p.bank_name || "",
                          account_number: p.account_number || "",
                          ifsc_code: p.ifsc_code || "",
                          branch_name: p.branch_name || "",
                          account_type: p.account_type || "savings",
                          primary_account: p.primary_account,
                        })
                      }}
                    >
                      Edit
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : employeeId ? (
            <p className="text-xs text-muted-foreground">No bank accounts on file for this employee.</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}

function NomineesSection({ canManage, employees }) {
  const [employeeId, setEmployeeId] = useState("")
  const [nominees, setNominees] = useState([])
  const [form, setForm] = useState(EMPTY_NOMINEE)
  const [editingId, setEditingId] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  usePageLoading(loading)

  useEffect(() => {
    if (!employeeId) {
      setNominees([])
      setLoading(false)
      return
    }
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        const data = await fetchJson(`/api/hr/employees/${employeeId}/nominees`)
        if (alive) setNominees(Array.isArray(data) ? data : [])
      } catch (e) {
        if (alive) setError(e.message || "Failed to load nominees")
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [employeeId])

  async function onSubmit(e) {
    e.preventDefault()
    if (!canManage || !employeeId) return
    try {
      setError("")
      const body = JSON.stringify({
        employee_nominee: {
          ...form,
          share_percent: form.share_percent === "" ? null : Number(form.share_percent),
        },
      })
      if (editingId) {
        await fetchJson(`/api/hr/employees/${employeeId}/nominees/${editingId}`, { method: "PUT", body })
      } else {
        await fetchJson(`/api/hr/employees/${employeeId}/nominees`, { method: "POST", body })
      }
      setNominees(await fetchJson(`/api/hr/employees/${employeeId}/nominees`))
      setForm(EMPTY_NOMINEE)
      setEditingId(null)
    } catch (e2) {
      setError(e2.message || "Save failed")
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Nominee details</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <p className="text-sm text-muted-foreground">Record nominees for gratuity, insurance, and statutory benefits.</p>
        <EmployeePicker employees={employees} selectedId={employeeId} onChange={setEmployeeId} />
        {error ? <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
        {canManage && employeeId ? (
          <form onSubmit={onSubmit} className="grid gap-3 md:grid-cols-2">
            <TextInput label="Nominee name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
            <TextInput label="Relationship" value={form.relationship} onChange={(v) => setForm((f) => ({ ...f, relationship: v }))} />
            <TextInput label="Date of birth" type="date" value={form.date_of_birth} onChange={(v) => setForm((f) => ({ ...f, date_of_birth: v }))} />
            <TextInput label="Phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
            <TextInput label="Share %" value={form.share_percent} onChange={(v) => setForm((f) => ({ ...f, share_percent: v }))} />
            <div className="md:col-span-2">
              <TextInput label="Address" value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))} />
            </div>
            <Button type="submit" className="md:col-span-2">
              {editingId ? "Update nominee" : "Add nominee"}
            </Button>
          </form>
        ) : null}
        {nominees.length > 0 ? (
          <ul className="space-y-2 text-xs">
            {nominees.map((n) => (
              <li key={n.id} className="rounded-lg border border-border/90 p-2">
                <div className="font-medium">
                  {n.name} ({n.relationship || "—"}){n.share_percent != null ? ` — ${n.share_percent}%` : ""}
                </div>
              </li>
            ))}
          </ul>
        ) : employeeId ? (
          <p className="text-xs text-muted-foreground">No nominees recorded.</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function EmployeeManagementPage(props) {
  const { active_slug, section, section_title, user_name, can_manage_employees, is_viewer, current_organization } = props
  const canManage = Boolean(can_manage_employees) && !is_viewer
  const [employees, setEmployees] = useState([])
  const [loadingEmployees, setLoadingEmployees] = useState(true)
  usePageLoading(loadingEmployees)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoadingEmployees(true)
        const data = await fetchJson("/api/hr/employees")
        if (alive) setEmployees(Array.isArray(data) ? data : [])
      } catch {
        if (alive) setEmployees([])
      } finally {
        if (alive) setLoadingEmployees(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [section])

  const body = useMemo(() => {
    switch (section) {
      case "salary-structures":
        return <SalaryStructuresSection canManage={canManage} employees={employees} />
      case "banking":
        return <BankingSection canManage={canManage} employees={employees} />
      case "nominees":
        return <NomineesSection canManage={canManage} employees={employees} />
      default:
        return <EmployeesSection canManage={canManage} currentOrganization={current_organization} />
    }
  }, [section, canManage, employees, current_organization])

  return (
    <BookShell
      activeSlug={active_slug || "employee-management"}
      userName={user_name}
      access={accessFromProps(props)}
    >
      <div className="flex flex-col gap-6">
        <PageHeader context="Employee management" title={section_title} />
        {body}
      </div>
    </BookShell>
  )
}
